import { useCallback } from 'react';
import { type SetSearchParams } from './useExtendSearchParams';
import { useMutableObject } from './useMutableObject';

export function useSortFromSearchParams<SB extends string = string>(
    searchParams: URLSearchParams,
    setSearchParams: SetSearchParams,
    options: {
        defaultSortBy?: string;
        defaultSortOrder?: 'asc' | 'desc';
        resetParams: string[];
        sortByKey?: string;
        sortOrderKey?: string;
    }
) {
    /** По умолчанию сортировка по полю 'created' и от нового к старому */
    const {
        defaultSortBy = 'created',
        defaultSortOrder = 'desc',
        resetParams = [],
        sortByKey = 'sortBy',
        sortOrderKey = 'sortOrder',
    } = options;

    const ref = useMutableObject(resetParams);

    const setSort = useCallback(
        (sortBy: SB, sortOrder: 'asc' | 'desc' = defaultSortOrder) => {
            setSearchParams((params) => {
                ref.current.forEach((key) => params.delete(key));

                if (sortBy === defaultSortBy) {
                    params.delete(sortByKey);
                } else {
                    params.set(sortByKey, sortBy);
                }

                if (!isSortOrder(sortOrder) || sortOrder === defaultSortOrder) {
                    params.delete(sortOrderKey);
                } else {
                    params.set(sortOrderKey, sortOrder);
                }

                return [params];
            });
        },
        [defaultSortBy, defaultSortOrder, ref, setSearchParams, sortByKey, sortOrderKey]
    );

    return {
        sortBy: (searchParams.get(sortByKey) || defaultSortBy) as SB,
        sortOrder: getSortOrder(searchParams.get(sortOrderKey), defaultSortOrder),
        setSort,
    };
}

function isSortOrder(sortOrder: unknown): sortOrder is 'asc' | 'desc' {
    return sortOrder === 'asc' || sortOrder === 'desc';
}

function getSortOrder(sortOrder: unknown, defValue: 'asc' | 'desc'): 'asc' | 'desc' {
    return isSortOrder(sortOrder) ? sortOrder : defValue;
}
